/**
 * Real Dashboard Metrics Hook
 * 
 * Provides real metrics from Supabase for the Executive Dashboard.
 * Replaces useExecutiveDemoData with actual database queries.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth-context';

interface DashboardMetrics {
  // Financial metrics
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  
  // Transaction metrics
  totalTransactions: number;
  pendingTransactions: number;
  processedTransactions: number;
  automationRate: number;
  
  // Learning metrics
  totalRules: number;
  activeRules: number;
  rulesLearnedThisWeek: number;
  averageConfidence: number;
  
  // Client metrics
  totalClients: number;
  activeMandates: number;
  completedBookings: number;
  
  // Performance metrics
  averageProcessingTime: number;
  errorRate: number;
  userSatisfaction: number;
}

interface UseDashboardMetricsReturn {
  metrics: DashboardMetrics;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDashboardMetrics = (): UseDashboardMetricsReturn => {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0,
    totalTransactions: 0,
    pendingTransactions: 0,
    processedTransactions: 0,
    automationRate: 0,
    totalRules: 0,
    activeRules: 0,
    rulesLearnedThisWeek: 0,
    averageConfidence: 0,
    totalClients: 0,
    activeMandates: 0,
    completedBookings: 0,
    averageProcessingTime: 0,
    errorRate: 0,
    userSatisfaction: 0,
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { organization } = useAuth();

  const fetchMetrics = async () => {
    if (!organization?.id) return;
    
    try {
      setIsLoading(true);
      setError(null);

      // Fetch all metrics in parallel
      const [
        financialMetrics,
        transactionMetrics,
        learningMetrics,
        clientMetrics,
        performanceMetrics
      ] = await Promise.all([
        fetchFinancialMetrics(organization.id),
        fetchTransactionMetrics(organization.id),
        fetchLearningMetrics(organization.id),
        fetchClientMetrics(organization.id),
        fetchPerformanceMetrics(organization.id)
      ]);

      setMetrics({
        ...financialMetrics,
        ...transactionMetrics,
        ...learningMetrics,
        ...clientMetrics,
        ...performanceMetrics
      });
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFinancialMetrics = async (organizationId: string) => {
    // Get revenue from journal entries with revenue accounts
    const { data: revenueData } = await supabase
      .from('booking_entries')
      .select('credit_amount, debit_amount')
      .eq('organization_id', organizationId)
      .in('account_number', ['3200', '3201', '3202', '3203']) // Revenue accounts
      .gte('created_at', new Date(new Date().getFullYear(), 0, 1).toISOString());

    // Get expenses from journal entries with expense accounts
    const { data: expenseData } = await supabase
      .from('booking_entries')
      .select('debit_amount, credit_amount')
      .eq('organization_id', organizationId)
      .in('account_number', ['4000', '4001', '4002', '4003', '5000', '5001', '5002', '5003']) // Expense accounts
      .gte('created_at', new Date(new Date().getFullYear(), 0, 1).toISOString());

    const totalRevenue = revenueData?.reduce((sum, entry) => sum + Number(entry.credit_amount || 0), 0) || 0;
    const totalExpenses = expenseData?.reduce((sum, entry) => sum + Number(entry.debit_amount || 0), 0) || 0;
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      profitMargin
    };
  };

  const fetchTransactionMetrics = async (organizationId: string) => {
    const { data: transactions } = await supabase
      .from('transactions')
      .select('id, human_verified, ai_classification_confidence, created_at')
      .eq('organization_id', organizationId);

    const totalTransactions = transactions?.length || 0;
    const pendingTransactions = transactions?.filter(t => !t.human_verified).length || 0;
    const processedTransactions = transactions?.filter(t => t.human_verified).length || 0;
    const automationRate = totalTransactions > 0 ? (processedTransactions / totalTransactions) * 100 : 0;

    return {
      totalTransactions,
      pendingTransactions,
      processedTransactions,
      automationRate
    };
  };

  const fetchLearningMetrics = async (organizationId: string) => {
    const { data: rules } = await supabase
      .from('learned_rules')
      .select('id, is_active, current_confidence, created_at')
      .eq('organization_id', organizationId);

    const totalRules = rules?.length || 0;
    const activeRules = rules?.filter(r => r.is_active).length || 0;
    
    // Rules learned this week
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const rulesLearnedThisWeek = rules?.filter(r => 
      new Date(r.created_at) >= oneWeekAgo
    ).length || 0;

    const averageConfidence = rules && rules.length > 0 
      ? rules.reduce((sum, r) => sum + (r.current_confidence || 0), 0) / rules.length 
      : 0;

    return {
      totalRules,
      activeRules,
      rulesLearnedThisWeek,
      averageConfidence
    };
  };

  const fetchClientMetrics = async (organizationId: string) => {
    const { data: mandates } = await supabase
      .from('mandates')
      .select('id, status')
      .eq('organization_id', organizationId);

    const { data: journalEntries } = await supabase
      .from('journal_entries')
      .select('id, status')
      .eq('organization_id', organizationId)
      .eq('status', 'posted');

    const totalClients = mandates?.length || 0;
    const activeMandates = mandates?.filter(m => m.status === 'active').length || 0;
    const completedBookings = journalEntries?.length || 0;

    return {
      totalClients,
      activeMandates,
      completedBookings
    };
  };

  const fetchPerformanceMetrics = async (organizationId: string) => {
    // For now, return mock performance metrics
    // These would be calculated from actual processing data when available
    const averageProcessingTime = 2.5; // seconds
    const errorRate = 5.2; // percentage
    const userSatisfaction = 85; // percentage

    return {
      averageProcessingTime,
      errorRate,
      userSatisfaction
    };
  };

  useEffect(() => {
    fetchMetrics();
  }, [organization?.id]);

  return {
    metrics,
    isLoading,
    error,
    refetch: fetchMetrics
  };
};
